import { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  FileText,
  Calendar,
  DollarSign,
  Building,
  Download,
  User,
  AlertCircle,
  AlertTriangle,
  Clock,
  CheckCircle,
  XCircle,
  FileSignature,
  Globe,
  Edit,
  Loader2,
  ChevronDown,
  Eye,
  CreditCard,
  Truck,
  Link as LinkIcon,
  Ban,
  Banknote,
  MessageSquare,
  Send,
} from 'lucide-react';
import { adminQuotesAPI, depositsAPI } from '../../services/api';
import RefundPaymentModal from '../../components/admin/RefundPaymentModal';
import { formatDateTime as fmtDateTime, formatMoney } from '../../utils/format';

function formatDate(dateString) {
  if (!dateString) return 'N/A';
  // Handle timezone issues for dates that come as UTC midnight
  let normalized = dateString;
  if (typeof dateString === 'string') {
    const dateMatch = dateString.match(/^(\d{4}-\d{2}-\d{2})/);
    if (dateMatch) {
      normalized = dateMatch[1] + 'T12:00:00';
    }
  }
  const date = new Date(normalized);
  if (isNaN(date.getTime())) return 'N/A';
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function formatDateTime(dateString) {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return 'N/A';
  return fmtDateTime(date, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatCurrency(amount) {
  return formatMoney(amount || 0);
}

// Status badge styling based on CW status names
const getStatusBadgeClass = (status) => {
  const normalized = (status || '').toLowerCase();
  if (normalized === 'draft') return 'bg-neutral-100 dark:bg-neutral-700 text-neutral-700 dark:text-neutral-300';
  if (normalized === 'open') return 'bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300';
  if (normalized === 'viewed') return 'bg-purple-100 dark:bg-purple-900/50 text-purple-700 dark:text-purple-300';
  if (normalized.includes('accepted')) return 'bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300';
  if (normalized.includes('rejected') || normalized === 'lost') return 'bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-300';
  if (normalized === 'won') return 'bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300';
  if (normalized === 'expired') return 'bg-neutral-100 dark:bg-neutral-700 text-neutral-600 dark:text-neutral-400';
  return 'bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300';
};

export default function AdminQuoteDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [downloading, setDownloading] = useState(false);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [showLinkInvoiceModal, setShowLinkInvoiceModal] = useState(false);
  const [showWaiveModal, setShowWaiveModal] = useState(false);
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [invoiceId, setInvoiceId] = useState('');
  const [waiveReason, setWaiveReason] = useState('');
  const [selectedPaymentForRefund, setSelectedPaymentForRefund] = useState(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-quote-detail', id],
    queryFn: () => adminQuotesAPI.get(id),
  });

  // Fetch CW opportunity statuses for the dropdown
  const { data: statusesData } = useQuery({
    queryKey: ['opportunity-statuses'],
    queryFn: () => adminQuotesAPI.getOpportunityStatuses(),
  });

  const opportunityStatuses = statusesData?.data?.statuses || [];

  // Fetch deposit status
  const { data: depositData, refetch: refetchDeposit } = useQuery({
    queryKey: ['admin-deposit', id],
    queryFn: async () => {
      try {
        const response = await depositsAPI.getStatus(id);
        return response.data;
      } catch (err) {
        if (err.response?.status === 404) {
          return null;
        }
        throw err;
      }
    },
    enabled: !!id,
    retry: false,
  });
  const deposit = depositData?.deposit || null;

  // Fetch quote comments
  const { data: commentsData, refetch: refetchComments } = useQuery({
    queryKey: ['admin-quote-comments', id],
    queryFn: () => adminQuotesAPI.getComments(id),
    enabled: !!id,
  });
  const comments = commentsData?.data?.comments || [];
  const unreadCount = commentsData?.data?.unreadCount || 0;

  // Comment state
  const [showCommentsSection, setShowCommentsSection] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [replyingTo, setReplyingTo] = useState(null); // {commentId, sessionId, authorName}

  const quote = data?.data?.quote;
  const lineItems = data?.data?.lineItems || [];
  const signature = data?.data?.signature;
  const views = data?.data?.views || [];
  const cwOpportunity = data?.data?.cwOpportunity;

  // Helper function to extract custom field values from CW opportunity
  const getCustomFieldValue = (caption) => {
    if (!cwOpportunity?.customFields) return null;
    const field = cwOpportunity.customFields.find(
      f => f.caption?.toLowerCase() === caption.toLowerCase()
    );
    return field?.value ?? null;
  };

  // Extract custom fields from CW opportunity
  const expirationDate = getCustomFieldValue('quote expiration');
  const depositAmount = getCustomFieldValue('deposit amount');
  const paymentTerms = getCustomFieldValue('payment terms');
  const shippingMethod = getCustomFieldValue('shipping method');
  // CW returns full ISO timestamp; treat the expiration as end-of-day in the
  // browser's local TZ so a quote valid through 6/15 doesn't expire at midnight UTC.
  const isExpired = (() => {
    if (!expirationDate) return false;
    const datePart = String(expirationDate).split('T')[0];
    const [y, m, d] = datePart.split('-').map(Number);
    if (!y || !m || !d) return false;
    const endOfDay = new Date(y, m - 1, d, 23, 59, 59, 999);
    return endOfDay.getTime() < Date.now();
  })();

  const statusMutation = useMutation({
    mutationFn: (newStatus) => adminQuotesAPI.updateStatus(id, newStatus, 'quote'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-quote-detail', id] });
      setShowStatusDropdown(false);
    },
  });

  // Deposit admin mutations
  const linkInvoiceMutation = useMutation({
    mutationFn: (invoiceId) => depositsAPI.linkInvoice(deposit?.id, invoiceId),
    onSuccess: () => {
      refetchDeposit();
      setShowLinkInvoiceModal(false);
      setInvoiceId('');
    },
  });

  const waiveDepositMutation = useMutation({
    mutationFn: (reason) => depositsAPI.waive(deposit?.id, reason),
    onSuccess: () => {
      refetchDeposit();
      setShowWaiveModal(false);
      setWaiveReason('');
    },
  });

  // Comment mutations
  const addCommentMutation = useMutation({
    mutationFn: (data) => adminQuotesAPI.addComment(id, data),
    onSuccess: () => {
      refetchComments();
      setReplyText('');
      setReplyingTo(null);
    },
  });

  const markAllReadMutation = useMutation({
    mutationFn: () => adminQuotesAPI.markAllCommentsRead(id),
    onSuccess: () => {
      refetchComments();
    },
  });

  // Mark comments as read when section is expanded
  const handleExpandComments = () => {
    setShowCommentsSection(!showCommentsSection);
    if (!showCommentsSection && unreadCount > 0) {
      markAllReadMutation.mutate();
    }
  };

  const handleSubmitReply = (e) => {
    e.preventDefault();
    if (!replyText.trim() || !replyingTo) return;
    addCommentMutation.mutate({
      sessionId: replyingTo.sessionId,
      message: replyText,
      parentId: replyingTo.commentId,
    });
  };

  const handleDownloadPdf = async () => {
    setDownloading(true);
    try {
      const response = await adminQuotesAPI.downloadPdf(id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `quote-${quote?.qwDocNumber || id}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download PDF:', err);
      alert('Failed to download quote PDF');
    } finally {
      setDownloading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !quote) {
    const errorData = error?.response?.data;
    return (
      <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg p-6 text-center">
        <AlertCircle className="w-12 h-12 mx-auto mb-4 text-red-400" />
        <p className="text-red-700 dark:text-red-300 font-medium">
          {errorData?.error || 'Quote not found'}
        </p>
        <button onClick={() => {
          const returnUrl = sessionStorage.getItem('adminQuotesReturnUrl') || '/admin/quotes';
          sessionStorage.removeItem('adminQuotesReturnUrl');
          navigate(returnUrl, { state: { refresh: Date.now() } });
        }} className="btn-secondary btn-sm mt-4">
          Back to Quotes
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Back link */}
      <button
        onClick={() => {
          const returnUrl = sessionStorage.getItem('adminQuotesReturnUrl') || '/admin/quotes';
          sessionStorage.removeItem('adminQuotesReturnUrl');
          navigate(returnUrl, { state: { refresh: Date.now() } });
        }}
        className="inline-flex items-center gap-2 text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-white"
      >
        <ArrowLeft className="w-4 h-4" /> Back to quotes
      </button>

      {/* Header Card */}
      <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700">
        <div className="p-6 border-b border-neutral-200 dark:border-neutral-700">
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-xl font-bold text-neutral-900 dark:text-white">
                {quote.quoteName}
              </h1>
              <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">
                {quote.qwDocNumber && `${quote.qwDocNumber} • `}ID: {id.substring(0, 8)}...
              </p>
            </div>
            <div className="flex items-center gap-3">
              {/* Status Dropdown - uses CW statuses */}
              <div className="relative">
                <button
                  onClick={() => setShowStatusDropdown(!showStatusDropdown)}
                  className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium cursor-pointer hover:opacity-80 ${getStatusBadgeClass(quote.status)}`}
                  disabled={statusMutation.isPending}
                >
                  {statusMutation.isPending ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : null}
                  {quote.status}
                  <ChevronDown className="w-3 h-3" />
                </button>
                {showStatusDropdown && (
                  <div className="absolute right-0 mt-1 w-48 bg-white dark:bg-neutral-800 rounded-lg shadow-lg border border-neutral-200 dark:border-neutral-700 py-1 z-10 max-h-64 overflow-y-auto">
                    {opportunityStatuses.map((status) => (
                      <button
                        key={status.id}
                        onClick={() => statusMutation.mutate(status.name)}
                        className={`w-full text-left px-4 py-2 text-sm hover:bg-neutral-50 dark:hover:bg-neutral-700 dark:text-neutral-200 ${
                          quote.status?.toLowerCase() === status.name.toLowerCase() ? 'bg-neutral-100 dark:bg-neutral-700 font-medium' : ''
                        }`}
                      >
                        {status.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <button
                onClick={handleDownloadPdf}
                disabled={downloading}
                className="btn-secondary btn-sm"
              >
                {downloading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Download className="w-4 h-4" />
                )}
                {downloading ? 'Downloading...' : 'Download PDF'}
              </button>
              {quote.status?.toLowerCase() === 'draft' && (
                <Link
                  to={`/admin/quotes/${id}/edit`}
                  className="btn-primary btn-sm"
                >
                  <Edit className="w-4 h-4" />
                  Edit Quote
                </Link>
              )}
            </div>
          </div>
        </div>

        {/* Expired Warning Banner */}
        {isExpired && (
          <div className="mx-6 mt-4 p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg flex items-center gap-3">
            <AlertTriangle className="w-6 h-6 text-red-500 flex-shrink-0" />
            <div>
              <p className="text-red-800 dark:text-red-200 font-medium">This quote has expired</p>
              <p className="text-red-600 dark:text-red-300 text-sm">
                This quote expired on {formatDate(expirationDate)} and can no longer be accepted by the customer.
              </p>
            </div>
          </div>
        )}

        {/* Summary Cards */}
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/50 flex items-center justify-center">
                <Building className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="text-sm text-neutral-500 dark:text-neutral-400">Company</p>
                <Link
                  to={`/admin/companies/${quote.companyId}`}
                  className="font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
                >
                  {quote.companyName || 'N/A'}
                </Link>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-100 dark:bg-purple-900/50 flex items-center justify-center">
                <User className="w-5 h-5 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <p className="text-sm text-neutral-500 dark:text-neutral-400">Contact</p>
                <p className="font-medium dark:text-white">
                  {quote.contactName || 'N/A'}
                  {quote.contactEmail && (
                    <span className="text-sm text-neutral-500 dark:text-neutral-400 block">{quote.contactEmail}</span>
                  )}
                </p>
              </div>
            </div>
            {paymentTerms && (
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-neutral-100 dark:bg-neutral-700 rounded-lg flex items-center justify-center">
                  <FileText className="w-5 h-5 text-neutral-600 dark:text-neutral-400" />
                </div>
                <div>
                  <p className="text-sm text-neutral-500 dark:text-neutral-400">Payment Terms</p>
                  <p className="font-medium dark:text-white">{paymentTerms}</p>
                </div>
              </div>
            )}
            {shippingMethod && (
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-neutral-100 dark:bg-neutral-700 rounded-lg flex items-center justify-center">
                  <Truck className="w-5 h-5 text-neutral-600 dark:text-neutral-400" />
                </div>
                <div>
                  <p className="text-sm text-neutral-500 dark:text-neutral-400">Shipping Method</p>
                  <p className="font-medium dark:text-white">{shippingMethod}</p>
                </div>
              </div>
            )}
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-100 dark:bg-amber-900/50 flex items-center justify-center">
                <Calendar className="w-5 h-5 text-amber-600 dark:text-amber-400" />
              </div>
              <div>
                <p className="text-sm text-neutral-500 dark:text-neutral-400">Valid Until</p>
                <p className="font-medium dark:text-white">{formatDate(quote.validUntil)}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-100 dark:bg-green-900/50 flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <p className="text-sm text-neutral-500 dark:text-neutral-400">Total Value</p>
                <p className="font-medium text-lg dark:text-white">{formatCurrency(quote.total)}</p>
              </div>
            </div>
            {expirationDate && (
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isExpired ? 'bg-red-100 dark:bg-red-900/50' : 'bg-blue-100 dark:bg-blue-900/50'}`}>
                  <Clock className={`w-5 h-5 ${isExpired ? 'text-red-600 dark:text-red-400' : 'text-blue-600 dark:text-blue-400'}`} />
                </div>
                <div>
                  <p className="text-sm text-neutral-500 dark:text-neutral-400">Quote Expires</p>
                  <p className={`font-medium ${isExpired ? 'text-red-600 dark:text-red-400' : 'dark:text-white'}`}>
                    {formatDate(expirationDate)}
                    {isExpired && <span className="ml-2 text-xs font-semibold">(EXPIRED)</span>}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Description/Notes */}
          {(quote.description || quote.notes) && (
            <div className="border-t border-neutral-200 dark:border-neutral-700 pt-6">
              <h3 className="font-medium text-neutral-900 dark:text-white mb-2">Notes</h3>
              <p className="text-neutral-600 dark:text-neutral-300 whitespace-pre-wrap">
                {quote.description || quote.notes}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Line Items */}
      <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700">
        <div className="px-6 py-4 border-b border-neutral-200 dark:border-neutral-700">
          <h2 className="text-lg font-semibold text-neutral-900 dark:text-white">
            Line Items ({lineItems.length})
          </h2>
        </div>

        {lineItems.length === 0 ? (
          <div className="p-8 text-center text-neutral-500 dark:text-neutral-400">
            <FileText className="w-12 h-12 mx-auto mb-3 text-neutral-300 dark:text-neutral-600" />
            <p>No line items in this quote</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-neutral-50 dark:bg-neutral-700/50 border-b border-neutral-200 dark:border-neutral-700">
                  <th className="text-left px-6 py-3 text-sm font-medium text-neutral-500 dark:text-neutral-400">Item</th>
                  <th className="text-center px-4 py-3 text-sm font-medium text-neutral-500 dark:text-neutral-400">Qty</th>
                  <th className="text-right px-4 py-3 text-sm font-medium text-neutral-500 dark:text-neutral-400">Price</th>
                  <th className="text-right px-6 py-3 text-sm font-medium text-neutral-500 dark:text-neutral-400">Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100 dark:divide-neutral-700">
                {lineItems.map((item) => {
                  const itemTotal = (parseFloat(item.unit_price) || 0) * (item.quantity || 1);
                  return (
                    <tr key={item.id} className="hover:bg-neutral-50 dark:hover:bg-neutral-700/50">
                      <td className="px-6 py-4">
                        <div className="font-medium text-neutral-900 dark:text-white">
                          {item.product_name || item.product_code || 'Item'}
                        </div>
                        {item.description && (
                          <div className="text-sm text-neutral-500 dark:text-neutral-400">{item.description}</div>
                        )}
                        <div className="flex gap-2 mt-1">
                          {item.is_optional && (
                            <span className="text-xs px-2 py-0.5 rounded bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-300">Optional</span>
                          )}
                          {item.package_group && (
                            <span className="text-xs px-2 py-0.5 rounded bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300">
                              {item.package_group}: {item.package_option}
                            </span>
                          )}
                          {item.is_recurring && (
                            <span className="text-xs px-2 py-0.5 rounded bg-purple-100 dark:bg-purple-900/50 text-purple-700 dark:text-purple-300">Recurring</span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-4 text-center text-neutral-600 dark:text-neutral-300">
                        {item.quantity || 1}
                      </td>
                      <td className="px-4 py-4 text-right text-neutral-600 dark:text-neutral-300">
                        {formatCurrency(item.unit_price)}
                      </td>
                      <td className="px-6 py-4 text-right font-medium text-neutral-900 dark:text-white">
                        {formatCurrency(itemTotal)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="border-t border-neutral-200 dark:border-neutral-700">
                  <td colSpan="3" className="px-6 py-2 text-right text-neutral-600 dark:text-neutral-300">Subtotal</td>
                  <td className="px-6 py-2 text-right font-medium dark:text-white">{formatCurrency(quote.subtotal)}</td>
                </tr>
                {parseFloat(quote.taxAmount) > 0 && (
                  <tr>
                    <td colSpan="3" className="px-6 py-2 text-right text-neutral-600 dark:text-neutral-300">Sales Tax</td>
                    <td className="px-6 py-2 text-right font-medium dark:text-white">{formatCurrency(quote.taxAmount)}</td>
                  </tr>
                )}
                <tr className="bg-neutral-50 dark:bg-neutral-700/50">
                  <td colSpan="3" className="px-6 py-4 text-right font-medium text-neutral-900 dark:text-white">Total</td>
                  <td className="px-6 py-4 text-right text-xl font-bold text-primary-600 dark:text-primary-400">
                    {formatCurrency(quote.total)}
                  </td>
                </tr>
                {depositAmount && depositAmount > 0 && (
                  <tr className="border-t border-neutral-200 dark:border-neutral-700">
                    <td colSpan="3" className="px-6 py-3 text-right text-neutral-600 dark:text-neutral-300">
                      <span className="flex items-center justify-end gap-2">
                        <CreditCard className="w-4 h-4" />
                        Deposit Required
                      </span>
                    </td>
                    <td className="px-6 py-3 text-right font-semibold text-amber-600 dark:text-amber-400">
                      {formatCurrency(depositAmount)}
                    </td>
                  </tr>
                )}
              </tfoot>
            </table>
          </div>
        )}
      </div>

      {/* E-Signature Section */}
      {signature && (
        <div className="bg-white dark:bg-neutral-800 rounded-lg border border-green-200 dark:border-green-800">
          <div className="px-6 py-4 border-b border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/30">
            <div className="flex items-center gap-2">
              <FileSignature className="w-5 h-5 text-green-600 dark:text-green-400" />
              <h2 className="text-lg font-semibold text-green-900 dark:text-green-300">E-Signature</h2>
            </div>
          </div>

          <div className="p-6">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Signature Display */}
              <div>
                <p className="text-sm text-neutral-500 dark:text-neutral-400 mb-3">Signature</p>
                <div className="border border-neutral-200 dark:border-neutral-700 rounded-lg p-4 bg-neutral-50 dark:bg-neutral-700/50">
                  {signature.signature_type === 'drawn' && signature.signature_data ? (
                    <img
                      src={signature.signature_data}
                      alt="Signature"
                      className="max-h-24"
                    />
                  ) : (
                    <p
                      className="text-2xl text-neutral-800 dark:text-neutral-200"
                      style={{ fontFamily: "'Brush Script MT', 'Segoe Script', cursive" }}
                    >
                      {signature.signature_data}
                    </p>
                  )}
                </div>
              </div>

              {/* Signer Details */}
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-neutral-500 dark:text-neutral-400">Signed By</p>
                  <p className="font-medium text-neutral-900 dark:text-white">{signature.signer_name}</p>
                  {signature.signer_title && (
                    <p className="text-sm text-neutral-600 dark:text-neutral-300">{signature.signer_title}</p>
                  )}
                  <p className="text-sm text-neutral-500 dark:text-neutral-400">{signature.signer_email}</p>
                </div>
                <div>
                  <p className="text-sm text-neutral-500 dark:text-neutral-400">Signed On</p>
                  <p className="font-medium text-neutral-900 dark:text-white">{formatDateTime(signature.signed_at)}</p>
                </div>
              </div>
            </div>

            {/* Audit Info */}
            <div className="mt-6 pt-6 border-t border-neutral-200 dark:border-neutral-700">
              <h4 className="text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">Audit Details</h4>
              <div className="space-y-1">
                <p className="text-xs text-neutral-500 dark:text-neutral-400 flex items-center gap-2">
                  <Globe className="w-4 h-4" />
                  <span className="font-medium">IP Address:</span> {signature.ip_address || 'N/A'}
                </p>
                <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1 truncate" title={signature.user_agent || ''}>
                  <span className="font-medium">User Agent:</span> {signature.user_agent ? signature.user_agent.substring(0, 80) + '...' : 'N/A'}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Decision Info (for non-signature approvals) */}
      {quote.status?.toLowerCase() === 'accepted from portal' && !signature && quote.decisionByName && (
        <div className="bg-white dark:bg-neutral-800 rounded-lg border border-green-200 dark:border-green-800">
          <div className="px-6 py-4 border-b border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/30">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
              <h2 className="text-lg font-semibold text-green-900 dark:text-green-300">Approval Details</h2>
            </div>
          </div>
          <div className="p-6">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-neutral-500 dark:text-neutral-400">Approved By</p>
                <p className="font-medium dark:text-white">{quote.decisionByName}</p>
                {quote.decisionByEmail && (
                  <p className="text-sm text-neutral-500 dark:text-neutral-400">{quote.decisionByEmail}</p>
                )}
              </div>
              <div>
                <p className="text-sm text-neutral-500 dark:text-neutral-400">Approved On</p>
                <p className="font-medium dark:text-white">{formatDateTime(quote.decisionAt)}</p>
              </div>
            </div>
            {quote.decisionNotes && (
              <div className="mt-4">
                <p className="text-sm text-neutral-500 dark:text-neutral-400">Notes</p>
                <p className="text-neutral-700 dark:text-neutral-300">{quote.decisionNotes}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Rejection Info */}
      {quote.status?.toLowerCase() === 'rejected from portal' && (
        <div className="bg-white dark:bg-neutral-800 rounded-lg border border-red-200 dark:border-red-800">
          <div className="px-6 py-4 border-b border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/30">
            <div className="flex items-center gap-2">
              <XCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
              <h2 className="text-lg font-semibold text-red-900 dark:text-red-300">Quote Declined</h2>
            </div>
          </div>
          <div className="p-6">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-neutral-500 dark:text-neutral-400">Declined By</p>
                <p className="font-medium dark:text-white">{quote.decisionByName || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-neutral-500 dark:text-neutral-400">Declined On</p>
                <p className="font-medium dark:text-white">{formatDateTime(quote.decisionAt)}</p>
              </div>
            </div>
            {quote.decisionNotes && (
              <div className="mt-4">
                <p className="text-sm text-neutral-500 dark:text-neutral-400">Reason</p>
                <p className="text-neutral-700 dark:text-neutral-300">{quote.decisionNotes}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Deposit Management Section */}
      {deposit && (
        <div className={`bg-white dark:bg-neutral-800 rounded-lg border ${
          deposit.status === 'paid'
            ? 'border-green-200 dark:border-green-800'
            : deposit.status === 'waived'
            ? 'border-neutral-200 dark:border-neutral-700'
            : 'border-amber-200 dark:border-amber-800'
        }`}>
          <div className={`px-6 py-4 border-b ${
            deposit.status === 'paid'
              ? 'border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/30'
              : deposit.status === 'waived'
              ? 'border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-700/50'
              : 'border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/30'
          }`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Banknote className={`w-5 h-5 ${
                  deposit.status === 'paid'
                    ? 'text-green-600 dark:text-green-400'
                    : deposit.status === 'waived'
                    ? 'text-neutral-600 dark:text-neutral-400'
                    : 'text-amber-600 dark:text-amber-400'
                }`} />
                <h2 className={`text-lg font-semibold ${
                  deposit.status === 'paid'
                    ? 'text-green-900 dark:text-green-300'
                    : deposit.status === 'waived'
                    ? 'text-neutral-900 dark:text-neutral-300'
                    : 'text-amber-900 dark:text-amber-300'
                }`}>
                  Deposit Information
                </h2>
              </div>
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                deposit.status === 'paid'
                  ? 'bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300'
                  : deposit.status === 'partial'
                  ? 'bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-300'
                  : deposit.status === 'waived'
                  ? 'bg-neutral-100 dark:bg-neutral-700 text-neutral-600 dark:text-neutral-400'
                  : deposit.status === 'refunded'
                  ? 'bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-300'
                  : 'bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-300'
              }`}>
                {deposit.status.charAt(0).toUpperCase() + deposit.status.slice(1)}
              </span>
            </div>
          </div>

          <div className="p-6">
            {/* Check Indicated Banner */}
            {deposit.sendingCheck && (
              <div className="mb-6 flex items-start gap-3 p-4 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg">
                <span className="text-blue-500 text-lg">✉️</span>
                <div>
                  <p className="font-medium text-blue-900 dark:text-blue-200">Customer indicated they will mail a check</p>
                  {deposit.sendingCheckAt && (
                    <p className="text-sm text-blue-700 dark:text-blue-300 mt-0.5">
                      Indicated on {new Date(deposit.sendingCheckAt).toLocaleString()}
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Deposit Summary */}
            <div className="grid md:grid-cols-4 gap-6 mb-6">
              <div>
                <p className="text-sm text-neutral-500 dark:text-neutral-400">Required</p>
                <p className="text-xl font-bold text-neutral-900 dark:text-white">
                  {formatCurrency(deposit.depositAmount)}
                </p>
              </div>
              <div>
                <p className="text-sm text-neutral-500 dark:text-neutral-400">Paid</p>
                <p className={`text-xl font-bold ${
                  deposit.amountPaid > 0 ? 'text-green-600 dark:text-green-400' : 'text-neutral-400'
                }`}>
                  {formatCurrency(deposit.amountPaid)}
                </p>
              </div>
              <div>
                <p className="text-sm text-neutral-500 dark:text-neutral-400">Remaining</p>
                <p className={`text-xl font-bold ${
                  deposit.amountRemaining > 0 ? 'text-amber-600 dark:text-amber-400' : 'text-green-600 dark:text-green-400'
                }`}>
                  {formatCurrency(deposit.amountRemaining)}
                </p>
              </div>
              <div>
                <p className="text-sm text-neutral-500 dark:text-neutral-400">Accepted Via</p>
                <p className="font-medium dark:text-white capitalize">
                  {deposit.acceptedVia?.replace('_', ' ') || 'N/A'}
                </p>
              </div>
            </div>

            {/* Signer Info */}
            {(deposit.signerName || deposit.signerEmail) && (
              <div className="grid md:grid-cols-2 gap-4 mb-6 pb-6 border-b border-neutral-200 dark:border-neutral-700">
                <div>
                  <p className="text-sm text-neutral-500 dark:text-neutral-400">Signer</p>
                  <p className="font-medium dark:text-white">{deposit.signerName || 'N/A'}</p>
                  {deposit.signerEmail && (
                    <p className="text-sm text-neutral-500 dark:text-neutral-400">{deposit.signerEmail}</p>
                  )}
                </div>
                {deposit.cwInvoiceId && (
                  <div>
                    <p className="text-sm text-neutral-500 dark:text-neutral-400">Linked CW Invoice</p>
                    <p className="font-medium text-primary-600 dark:text-primary-400">
                      Invoice #{deposit.cwInvoiceId}
                      <span className="text-xs text-neutral-500 dark:text-neutral-400 ml-2">
                        (Linked {formatDate(deposit.cwInvoiceLinkedAt)})
                      </span>
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Payment History */}
            {deposit.payments && deposit.payments.length > 0 && (
              <div className="mb-6">
                <h4 className="font-medium text-neutral-900 dark:text-white mb-3">Payment History</h4>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-neutral-500 dark:text-neutral-400 border-b border-neutral-200 dark:border-neutral-700">
                        <th className="pb-2 font-medium">Date</th>
                        <th className="pb-2 font-medium">Amount</th>
                        <th className="pb-2 font-medium">Method</th>
                        <th className="pb-2 font-medium">Paid By</th>
                        <th className="pb-2 font-medium">Transaction ID</th>
                        <th className="pb-2 font-medium">Status</th>
                        <th className="pb-2 font-medium"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-neutral-100 dark:divide-neutral-700">
                      {deposit.payments.map((payment) => (
                        <tr key={payment.id} className="hover:bg-neutral-50 dark:hover:bg-neutral-700/50">
                          <td className="py-3 text-neutral-900 dark:text-white">
                            {formatDateTime(payment.paymentDate)}
                          </td>
                          <td className="py-3 font-medium text-neutral-900 dark:text-white">
                            {formatCurrency(payment.amount)}
                          </td>
                          <td className="py-3 text-neutral-600 dark:text-neutral-300">
                            {(() => {
                              const isAch = payment.paymentSource === 'one_time_ach' || payment.cardType === 'ACH';
                              const last4 = payment.cardLastFour ? ` ****${payment.cardLastFour}` : '';
                              const main = isAch ? `ACH${last4}` : `${payment.cardType || 'Card'}${last4}`;
                              return (
                                <>
                                  <div className="font-medium">{main}</div>
                                  {payment.bankName && (
                                    <div className="text-xs text-neutral-500 dark:text-neutral-400">{payment.bankName}</div>
                                  )}
                                  {payment.billingCompany && (
                                    <div className="text-xs text-neutral-500 dark:text-neutral-400">{payment.billingCompany}</div>
                                  )}
                                </>
                              );
                            })()}
                          </td>
                          <td className="py-3">
                            <div className="text-neutral-900 dark:text-white">{payment.paidByName}</div>
                            {payment.paidByEmail && (
                              <div className="text-xs text-neutral-500 dark:text-neutral-400">{payment.paidByEmail}</div>
                            )}
                          </td>
                          <td className="py-3 font-mono text-xs text-neutral-500 dark:text-neutral-400">
                            {payment.transactionId || 'N/A'}
                          </td>
                          <td className="py-3">
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                              payment.status === 'completed'
                                ? 'bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300'
                                : payment.status === 'refunded'
                                ? 'bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-300'
                                : 'bg-neutral-100 dark:bg-neutral-700 text-neutral-700 dark:text-neutral-300'
                            }`}>
                              {payment.status}
                            </span>
                          </td>
                          <td className="py-3">
                            {payment.status === 'completed' && (
                              <button
                                onClick={() => {
                                  setSelectedPaymentForRefund(payment);
                                  setShowRefundModal(true);
                                }}
                                className="text-sm text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                              >
                                Refund
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Admin Actions */}
            {deposit.status !== 'paid' && deposit.status !== 'refunded' && deposit.status !== 'waived' && (
              <div className="flex flex-wrap gap-3 pt-4 border-t border-neutral-200 dark:border-neutral-700">
                {!deposit.cwInvoiceId && (
                  <button
                    onClick={() => setShowLinkInvoiceModal(true)}
                    className="btn-secondary btn-sm"
                  >
                    <LinkIcon className="w-4 h-4" />
                    Link to CW Invoice
                  </button>
                )}
                <button
                  onClick={() => setShowWaiveModal(true)}
                  className="btn-secondary btn-sm"
                >
                  <Ban className="w-4 h-4" />
                  Waive Deposit
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Link Invoice Modal */}
      {showLinkInvoiceModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-xl max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-neutral-200 dark:border-neutral-700">
              <h3 className="text-lg font-semibold dark:text-white">Link to ConnectWise Invoice</h3>
            </div>
            <div className="p-6">
              <p className="text-sm text-neutral-600 dark:text-neutral-300 mb-4">
                Enter the ConnectWise Down Payment Invoice ID to link this deposit for reconciliation.
              </p>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                Invoice ID
              </label>
              <input
                type="number"
                value={invoiceId}
                onChange={(e) => setInvoiceId(e.target.value)}
                placeholder="Enter CW Invoice ID"
                className="input w-full"
              />
              {linkInvoiceMutation.error && (
                <p className="text-sm text-red-600 dark:text-red-400 mt-2">
                  {linkInvoiceMutation.error.response?.data?.error || 'Failed to link invoice'}
                </p>
              )}
            </div>
            <div className="px-6 py-4 border-t border-neutral-200 dark:border-neutral-700 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowLinkInvoiceModal(false);
                  setInvoiceId('');
                }}
                className="btn-secondary btn-sm"
                disabled={linkInvoiceMutation.isPending}
              >
                Cancel
              </button>
              <button
                onClick={() => linkInvoiceMutation.mutate(parseInt(invoiceId))}
                disabled={!invoiceId || linkInvoiceMutation.isPending}
                className="btn-primary btn-sm"
              >
                {linkInvoiceMutation.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Linking...
                  </>
                ) : (
                  'Link Invoice'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Waive Deposit Modal */}
      {showWaiveModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-xl max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-neutral-200 dark:border-neutral-700">
              <h3 className="text-lg font-semibold dark:text-white">Waive Deposit</h3>
            </div>
            <div className="p-6">
              <div className="flex items-center gap-3 p-4 bg-amber-50 dark:bg-amber-900/30 rounded-lg mb-4">
                <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0" />
                <p className="text-sm text-amber-800 dark:text-amber-200">
                  This will mark the deposit as waived. The customer will no longer be required to pay.
                </p>
              </div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                Reason (optional)
              </label>
              <textarea
                value={waiveReason}
                onChange={(e) => setWaiveReason(e.target.value)}
                placeholder="Enter reason for waiving deposit..."
                rows={3}
                className="input w-full"
              />
              {waiveDepositMutation.error && (
                <p className="text-sm text-red-600 dark:text-red-400 mt-2">
                  {waiveDepositMutation.error.response?.data?.error || 'Failed to waive deposit'}
                </p>
              )}
            </div>
            <div className="px-6 py-4 border-t border-neutral-200 dark:border-neutral-700 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowWaiveModal(false);
                  setWaiveReason('');
                }}
                className="btn-secondary btn-sm"
                disabled={waiveDepositMutation.isPending}
              >
                Cancel
              </button>
              <button
                onClick={() => waiveDepositMutation.mutate(waiveReason)}
                disabled={waiveDepositMutation.isPending}
                className="bg-amber-600 hover:bg-amber-700 text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2"
              >
                {waiveDepositMutation.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Waiving...
                  </>
                ) : (
                  'Waive Deposit'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Refund Deposit Modal */}
      {showRefundModal && selectedPaymentForRefund && (
        <RefundPaymentModal
          source="deposit"
          paymentId={selectedPaymentForRefund.id}
          onDone={() => refetchDeposit()}
          onClose={() => {
            setShowRefundModal(false);
            setSelectedPaymentForRefund(null);
          }}
        />
      )}

      {/* View History */}
      <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700">
        <div className="px-6 py-4 border-b border-neutral-200 dark:border-neutral-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Eye className="w-5 h-5 text-purple-600 dark:text-purple-400" />
              <h2 className="text-lg font-semibold text-neutral-900 dark:text-white">View History</h2>
            </div>
            <div className="flex items-center gap-4 text-sm text-neutral-600 dark:text-neutral-300">
              <span><strong>{quote.viewCount || 0}</strong> total views</span>
              {quote.firstViewedAt && (
                <span>First viewed: {formatDateTime(quote.firstViewedAt)}</span>
              )}
            </div>
          </div>
        </div>
        <div className="p-6">
          {views.length === 0 ? (
            <p className="text-neutral-500 dark:text-neutral-400 text-center py-4">No views recorded yet</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-neutral-500 dark:text-neutral-400 border-b border-neutral-200 dark:border-neutral-700">
                    <th className="pb-2 font-medium">Viewed At</th>
                    <th className="pb-2 font-medium">Viewer</th>
                    <th className="pb-2 font-medium">Device</th>
                    <th className="pb-2 font-medium">Browser / OS</th>
                    <th className="pb-2 font-medium">Language</th>
                    <th className="pb-2 font-medium">Engagement</th>
                    <th className="pb-2 font-medium">Session</th>
                    <th className="pb-2 font-medium">IP</th>
                    <th className="pb-2 font-medium">Referrer</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-100 dark:divide-neutral-700">
                  {views.map((view) => (
                    <tr key={view.id} className="hover:bg-neutral-50 dark:hover:bg-neutral-700/50">
                      <td className="py-3 text-neutral-900 dark:text-white whitespace-nowrap">{formatDateTime(view.viewedAt)}</td>
                      <td className="py-3">
                        <div className="text-neutral-700 dark:text-neutral-200">{view.viewerName || 'Anonymous'}</div>
                        {view.viewerEmail && (
                          <div className="text-neutral-500 dark:text-neutral-400 text-xs">{view.viewerEmail}</div>
                        )}
                      </td>
                      <td className="py-3">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                          view.deviceType === 'mobile' ? 'bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300' :
                          view.deviceType === 'tablet' ? 'bg-purple-100 dark:bg-purple-900/50 text-purple-700 dark:text-purple-300' :
                          'bg-neutral-100 dark:bg-neutral-700 text-neutral-700 dark:text-neutral-300'
                        }`}>
                          {view.deviceType || 'desktop'}
                        </span>
                      </td>
                      <td className="py-3 text-neutral-600 dark:text-neutral-300 text-xs">
                        <div>{view.browser} {view.browserVersion}</div>
                        <div className="text-neutral-400 dark:text-neutral-500">{view.os} {view.osVersion}</div>
                      </td>
                      <td className="py-3 text-neutral-600 dark:text-neutral-300 text-xs">
                        {view.language || '-'}
                      </td>
                      <td className="py-3">
                        <div className="flex flex-wrap gap-1">
                          {view.timeOnPage && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-neutral-100 dark:bg-neutral-700 text-neutral-600 dark:text-neutral-300">
                              {Math.floor(view.timeOnPage / 60)}m {view.timeOnPage % 60}s
                            </span>
                          )}
                          {view.scrolledToBottom && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300">
                              Read all
                            </span>
                          )}
                          {view.pdfDownloaded && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300">
                              PDF
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="py-3 text-neutral-500 dark:text-neutral-400 font-mono text-xs" title={view.sessionId || ''}>
                        {view.sessionId ? view.sessionId.substring(0, 8) + '...' : '-'}
                      </td>
                      <td className="py-3 text-neutral-500 dark:text-neutral-400 font-mono text-xs">{view.ipAddress || '-'}</td>
                      <td className="py-3 text-neutral-500 dark:text-neutral-400 text-xs max-w-[150px] truncate" title={view.referrerUrl || ''}>
                        {view.referrerUrl ? new URL(view.referrerUrl).pathname : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Customer Questions */}
      <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700">
        <button
          className="w-full px-6 py-4 flex items-center justify-between hover:bg-neutral-50 dark:hover:bg-neutral-700/50"
          onClick={handleExpandComments}
        >
          <div className="flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-amber-600 dark:text-amber-400" />
            <h2 className="text-lg font-semibold text-neutral-900 dark:text-white">Customer Questions</h2>
            {unreadCount > 0 && (
              <span className="inline-flex items-center justify-center px-2 py-0.5 text-xs font-medium rounded-full bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300">
                {unreadCount} new
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 text-sm text-neutral-500 dark:text-neutral-400">
            <span>{comments.length} {comments.length === 1 ? 'message' : 'messages'}</span>
            {showCommentsSection ? (
              <ChevronDown className="w-5 h-5 rotate-180" />
            ) : (
              <ChevronDown className="w-5 h-5" />
            )}
          </div>
        </button>

        {showCommentsSection && (
          <div className="border-t border-neutral-200 dark:border-neutral-700 p-6">
            {comments.length === 0 ? (
              <p className="text-neutral-500 dark:text-neutral-400 text-center py-4">
                No questions or comments from customers yet
              </p>
            ) : (
              <div className="space-y-4">
                {comments.map((comment) => (
                  <div
                    key={comment.id}
                    className={`p-4 rounded-lg ${
                      comment.author_type === 'internal'
                        ? 'bg-primary-50 dark:bg-primary-900/20 border border-primary-100 dark:border-primary-800 ml-8'
                        : 'bg-neutral-50 dark:bg-neutral-700/50 border border-neutral-100 dark:border-neutral-600'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        {comment.author_type === 'internal' ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-primary-100 dark:bg-primary-800 text-primary-700 dark:text-primary-200 text-xs font-medium rounded-full">
                            <Building className="w-3 h-3" />
                            {comment.author_name || 'Staff'}
                          </span>
                        ) : (
                          <>
                            <div className="w-6 h-6 bg-neutral-200 dark:bg-neutral-600 rounded-full flex items-center justify-center">
                              <User className="w-3 h-3 text-neutral-500 dark:text-neutral-300" />
                            </div>
                            <span className="font-medium text-sm text-neutral-900 dark:text-white">
                              {comment.author_name || 'Anonymous'}
                            </span>
                          </>
                        )}
                        {comment.author_email && comment.author_type === 'customer' && (
                          <span className="text-xs text-neutral-500 dark:text-neutral-400">
                            ({comment.author_email})
                          </span>
                        )}
                        <span className="text-xs text-neutral-500 dark:text-neutral-400">
                          {formatDateTime(comment.created_at)}
                        </span>
                      </div>
                      {comment.author_type === 'customer' && (
                        <button
                          onClick={() => setReplyingTo({
                            commentId: comment.id,
                            sessionId: comment.session_id,
                            authorName: comment.author_name
                          })}
                          className="text-xs text-primary-600 dark:text-primary-400 hover:underline"
                        >
                          Reply
                        </button>
                      )}
                    </div>
                    <p className="text-sm text-neutral-700 dark:text-neutral-200 whitespace-pre-wrap">
                      {comment.message}
                    </p>
                  </div>
                ))}

                {/* Reply Form */}
                {replyingTo && (
                  <form onSubmit={handleSubmitReply} className="mt-4 ml-8 p-4 bg-neutral-50 dark:bg-neutral-700/50 rounded-lg border border-neutral-200 dark:border-neutral-600">
                    <div className="text-xs text-neutral-500 dark:text-neutral-400 mb-2">
                      Replying to {replyingTo.authorName || 'customer'}
                    </div>
                    <textarea
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      placeholder="Type your response..."
                      className="input min-h-[80px] mb-3"
                      required
                    />
                    <div className="flex justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          setReplyingTo(null);
                          setReplyText('');
                        }}
                        className="btn-secondary text-sm"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="btn-primary text-sm"
                        disabled={addCommentMutation.isPending || !replyText.trim()}
                      >
                        {addCommentMutation.isPending ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            <Send className="w-4 h-4" /> Send Reply
                          </>
                        )}
                      </button>
                    </div>
                    {addCommentMutation.error && (
                      <p className="text-sm text-red-600 mt-2">
                        {addCommentMutation.error.response?.data?.error || 'Failed to send reply'}
                      </p>
                    )}
                  </form>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Terms & Conditions */}
      {quote.termsAndConditions && (
        <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-6">
          <h3 className="font-medium text-neutral-900 dark:text-white mb-4">Terms & Conditions</h3>
          <div className="prose prose-sm max-w-none text-neutral-600 dark:text-neutral-300">
            <pre className="whitespace-pre-wrap font-sans text-sm">
              {quote.termsAndConditions}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
}
